import React, { useState } from 'react';

const SettingsPage = () => {
  const [settings, setSettings] = useState({
    notifications: {
      mindfulBreaks: true,
      focusReminders: true,
      screenTimeAlerts: true,
      weeklyReports: true,
    },
    goals: {
      dailyScreenTime: 4,
      socialMediaTime: 1,
      mindfulBreaks: 5,
      focusSessions: 2,
    },
    sleepGuardian: {
      enabled: true,
      startTime: '22:00',
      endTime: '07:00',
      blockAllApps: false,
    },
    dataPrivacy: {
      collectUsageData: true,
      shareAnonymousStats: false,
    },
  });

  const handleNotificationChange = (setting) => {
    setSettings({
      ...settings,
      notifications: {
        ...settings.notifications,
        [setting]: !settings.notifications[setting],
      },
    });
  };

  const handleGoalChange = (setting, value) => {
    setSettings({
      ...settings,
      goals: {
        ...settings.goals,
        [setting]: value,
      },
    });
  };

  const handleSleepGuardianChange = (setting, value) => {
    setSettings({
      ...settings,
      sleepGuardian: {
        ...settings.sleepGuardian,
        [setting]: typeof value === 'boolean' ? value : value,
      },
    });
  };

  const handleDataPrivacyChange = (setting) => {
    setSettings({
      ...settings,
      dataPrivacy: {
        ...settings.dataPrivacy,
        [setting]: !settings.dataPrivacy[setting],
      },
    });
  };

  const handleSaveSettings = () => {
    // In a real app, this would save to localStorage or an API
    alert('Settings saved successfully!');
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="md:flex md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-display font-bold text-gray-900">Settings</h1>
          <p className="mt-1 text-lg text-gray-500">
            Customize your digital wellbeing experience
          </p>
        </div>
        <div className="mt-4 md:mt-0">
          <button
            onClick={handleSaveSettings}
            className="btn btn-primary"
          >
            Save Settings
          </button>
        </div>
      </div>

      <div className="mt-8 space-y-8">
        {/* Notifications Section */}
        <div className="card">
          <h2 className="text-xl font-medium text-gray-900 mb-6">Notifications</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-base font-medium text-gray-900">Mindful Break Reminders</h3>
                <p className="text-sm text-gray-500">Receive reminders to take breaks from screens</p>
              </div>
              <label className="inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={settings.notifications.mindfulBreaks}
                  onChange={() => handleNotificationChange('mindfulBreaks')}
                />
                <div className={`relative w-11 h-6 rounded-full transition-colors duration-200 ease-in-out ${settings.notifications.mindfulBreaks ? 'bg-primary' : 'bg-gray-200'}`}>
                  <div className={`absolute left-0.5 top-0.5 bg-white w-5 h-5 rounded-full transition-transform duration-200 ease-in-out ${settings.notifications.mindfulBreaks ? 'transform translate-x-5' : ''}`}></div>
                </div>
              </label>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-base font-medium text-gray-900">Focus Session Reminders</h3>
                <p className="text-sm text-gray-500">Get reminded to start your scheduled focus sessions</p>
              </div>
              <label className="inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={settings.notifications.focusReminders}
                  onChange={() => handleNotificationChange('focusReminders')}
                />
                <div className={`relative w-11 h-6 rounded-full transition-colors duration-200 ease-in-out ${settings.notifications.focusReminders ? 'bg-primary' : 'bg-gray-200'}`}>
                  <div className={`absolute left-0.5 top-0.5 bg-white w-5 h-5 rounded-full transition-transform duration-200 ease-in-out ${settings.notifications.focusReminders ? 'transform translate-x-5' : ''}`}></div>
                </div>
              </label>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-base font-medium text-gray-900">Screen Time Alerts</h3>
                <p className="text-sm text-gray-500">Receive alerts when you exceed your screen time goals</p>
              </div>
              <label className="inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={settings.notifications.screenTimeAlerts}
                  onChange={() => handleNotificationChange('screenTimeAlerts')}
                />
                <div className={`relative w-11 h-6 rounded-full transition-colors duration-200 ease-in-out ${settings.notifications.screenTimeAlerts ? 'bg-primary' : 'bg-gray-200'}`}>
                  <div className={`absolute left-0.5 top-0.5 bg-white w-5 h-5 rounded-full transition-transform duration-200 ease-in-out ${settings.notifications.screenTimeAlerts ? 'transform translate-x-5' : ''}`}></div>
                </div>
              </label>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-base font-medium text-gray-900">Weekly Reports</h3>
                <p className="text-sm text-gray-500">Get a weekly summary of your digital wellbeing</p>
              </div>
              <label className="inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={settings.notifications.weeklyReports}
                  onChange={() => handleNotificationChange('weeklyReports')}
                />
                <div className={`relative w-11 h-6 rounded-full transition-colors duration-200 ease-in-out ${settings.notifications.weeklyReports ? 'bg-primary' : 'bg-gray-200'}`}>
                  <div className={`absolute left-0.5 top-0.5 bg-white w-5 h-5 rounded-full transition-transform duration-200 ease-in-out ${settings.notifications.weeklyReports ? 'transform translate-x-5' : ''}`}></div>
                </div>
              </label>
            </div>
          </div>
        </div>

        {/* Goals Section */}
        <div className="card">
          <h2 className="text-xl font-medium text-gray-900 mb-6">Goals</h2>
          <div className="space-y-6">
            <div>
              <div className="flex justify-between items-center mb-2">
                <label htmlFor="daily-screen-time" className="block text-base font-medium text-gray-900">
                  Daily Screen Time (hours)
                </label>
                <span className="text-sm text-gray-500">{settings.goals.dailyScreenTime} hours</span>
              </div>
              <input
                type="range"
                id="daily-screen-time"
                min="1"
                max="12"
                step="0.5"
                value={settings.goals.dailyScreenTime}
                onChange={(e) => handleGoalChange('dailyScreenTime', parseFloat(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>1h</span>
                <span>6h</span>
                <span>12h</span>
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <label htmlFor="social-media-time" className="block text-base font-medium text-gray-900">
                  Social Media Time (hours)
                </label>
                <span className="text-sm text-gray-500">{settings.goals.socialMediaTime} hours</span>
              </div>
              <input
                type="range"
                id="social-media-time"
                min="0.5"
                max="6"
                step="0.5"
                value={settings.goals.socialMediaTime}
                onChange={(e) => handleGoalChange('socialMediaTime', parseFloat(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>0.5h</span>
                <span>3h</span>
                <span>6h</span>
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <label htmlFor="mindful-breaks" className="block text-base font-medium text-gray-900">
                  Mindful Breaks (per day)
                </label>
                <span className="text-sm text-gray-500">{settings.goals.mindfulBreaks} breaks</span>
              </div>
              <input
                type="range"
                id="mindful-breaks"
                min="1"
                max="10"
                step="1"
                value={settings.goals.mindfulBreaks}
                onChange={(e) => handleGoalChange('mindfulBreaks', parseInt(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>1</span>
                <span>5</span>
                <span>10</span>
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <label htmlFor="focus-sessions" className="block text-base font-medium text-gray-900">
                  Focus Sessions (per day)
                </label>
                <span className="text-sm text-gray-500">{settings.goals.focusSessions} sessions</span>
              </div>
              <input
                type="range"
                id="focus-sessions"
                min="1"
                max="8"
                step="1"
                value={settings.goals.focusSessions}
                onChange={(e) => handleGoalChange('focusSessions', parseInt(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>1</span>
                <span>4</span>
                <span>8</span>
              </div>
            </div>
          </div>
        </div>

        {/* Sleep Guardian Section */}
        <div className="card">
          <h2 className="text-xl font-medium text-gray-900 mb-6">Sleep Guardian</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-base font-medium text-gray-900">Enable Sleep Guardian</h3>
                <p className="text-sm text-gray-500">Reduce blue light and limit app usage before bedtime</p>
              </div>
              <label className="inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={settings.sleepGuardian.enabled}
                  onChange={() => handleSleepGuardianChange('enabled', !settings.sleepGuardian.enabled)}
                />
                <div className={`relative w-11 h-6 rounded-full transition-colors duration-200 ease-in-out ${settings.sleepGuardian.enabled ? 'bg-primary' : 'bg-gray-200'}`}>
                  <div className={`absolute left-0.5 top-0.5 bg-white w-5 h-5 rounded-full transition-transform duration-200 ease-in-out ${settings.sleepGuardian.enabled ? 'transform translate-x-5' : ''}`}></div>
                </div>
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="sleep-start-time" className="block text-sm font-medium text-gray-700 mb-1">
                  Start Time
                </label>
                <input
                  type="time"
                  id="sleep-start-time"
                  className="input"
                  value={settings.sleepGuardian.startTime}
                  onChange={(e) => handleSleepGuardianChange('startTime', e.target.value)}
                  disabled={!settings.sleepGuardian.enabled}
                />
              </div>
              <div>
                <label htmlFor="sleep-end-time" className="block text-sm font-medium text-gray-700 mb-1">
                  End Time
                </label>
                <input
                  type="time"
                  id="sleep-end-time"
                  className="input"
                  value={settings.sleepGuardian.endTime}
                  onChange={(e) => handleSleepGuardianChange('endTime', e.target.value)}
                  disabled={!settings.sleepGuardian.enabled}
                />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-base font-medium text-gray-900">Block All Apps</h3>
                <p className="text-sm text-gray-500">Block all non-essential apps during sleep hours</p>
              </div>
              <label className="inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={settings.sleepGuardian.blockAllApps}
                  onChange={() => handleSleepGuardianChange('blockAllApps', !settings.sleepGuardian.blockAllApps)}
                  disabled={!settings.sleepGuardian.enabled}
                />
                <div className={`relative w-11 h-6 rounded-full transition-colors duration-200 ease-in-out ${settings.sleepGuardian.blockAllApps ? 'bg-primary' : 'bg-gray-200'} ${!settings.sleepGuardian.enabled ? 'opacity-50' : ''}`}>
                  <div className={`absolute left-0.5 top-0.5 bg-white w-5 h-5 rounded-full transition-transform duration-200 ease-in-out ${settings.sleepGuardian.blockAllApps ? 'transform translate-x-5' : ''}`}></div>
                </div>
              </label>
            </div>
          </div>
        </div>

        {/* Data Privacy Section */}
        <div className="card">
          <h2 className="text-xl font-medium text-gray-900 mb-6">Data & Privacy</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-base font-medium text-gray-900">Collect Usage Data</h3>
                <p className="text-sm text-gray-500">Allow the app to collect data about your usage patterns</p>
              </div>
              <label className="inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={settings.dataPrivacy.collectUsageData}
                  onChange={() => handleDataPrivacyChange('collectUsageData')}
                />
                <div className={`relative w-11 h-6 rounded-full transition-colors duration-200 ease-in-out ${settings.dataPrivacy.collectUsageData ? 'bg-primary' : 'bg-gray-200'}`}>
                  <div className={`absolute left-0.5 top-0.5 bg-white w-5 h-5 rounded-full transition-transform duration-200 ease-in-out ${settings.dataPrivacy.collectUsageData ? 'transform translate-x-5' : ''}`}></div>
                </div>
              </label>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-base font-medium text-gray-900">Share Anonymous Statistics</h3>
                <p className="text-sm text-gray-500">Share anonymous usage data to help improve the app</p>
              </div>
              <label className="inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={settings.dataPrivacy.shareAnonymousStats}
                  onChange={() => handleDataPrivacyChange('shareAnonymousStats')}
                  disabled={!settings.dataPrivacy.collectUsageData}
                />
                <div className={`relative w-11 h-6 rounded-full transition-colors duration-200 ease-in-out ${settings.dataPrivacy.shareAnonymousStats ? 'bg-primary' : 'bg-gray-200'} ${!settings.dataPrivacy.collectUsageData ? 'opacity-50' : ''}`}>
                  <div className={`absolute left-0.5 top-0.5 bg-white w-5 h-5 rounded-full transition-transform duration-200 ease-in-out ${settings.dataPrivacy.shareAnonymousStats ? 'transform translate-x-5' : ''}`}></div>
                </div>
              </label>
            </div>

            <div className="mt-6 p-4 bg-blue-50 rounded-lg">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3 text-sm text-blue-700">
                  <p>
                    Your privacy is important to us. We only collect data that helps improve your digital wellbeing experience. 
                    All data is stored locally on your device unless you opt to share anonymous statistics.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <button
            onClick={handleSaveSettings}
            className="btn btn-primary px-8"
          >
            Save All Settings
          </button>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage; 